import React, { useState, useEffect } from 'react';
import { MapPin, List, Settings } from 'lucide-react';
import { Route, UserLocation } from './types';
import { RouteGroup as RouteGroupType } from './utils/locationUtils';
import { useRoutes } from './hooks/useRoutes';
import { useSearch } from './hooks/useSearch';
import { useMyRoutes } from './hooks/useMyRoutes';
import { useCrags } from './hooks/useCrags';
import { sortRoutesByDistance } from './utils';
import { groupRoutesByLocation } from './utils/locationUtils';
import SearchFilters from './components/SearchFilters';
import RouteCard from './components/RouteCard';
import RouteGroup from './components/RouteGroup';
import RouteMap from './components/RouteMap';
import MyRoutes from './components/MyRoutes';
import Pagination from './components/Pagination';
import SortByControl from './components/SortByControl';
import MapViewControl, { MapViewType } from './components/MapViewControl';
import Toast from './components/Toast';

type ViewMode = 'search' | 'map' | 'my-routes';

function App() {
  const { routes, loading, error } = useRoutes();
  const { crags, loading: cragsLoading, error: cragsError } = useCrags();
  const { 
    searchTerm, 
    setSearchTerm, 
    filters, 
    updateFilters, 
    clearFilters, 
    filteredRoutes, 
    groupedRoutes,
    totalResults,
    currentPage,
    totalPages,
    pageSize,
    goToPage,
    nextPage,
    prevPage,
  } = useSearch(routes);
  
  const {
    myRoutes,
    addRoute,
    removeRoute,
    isRouteInMyRoutes,
    clearAllRoutes,
  } = useMyRoutes();

  const [userLocation] = useState<UserLocation | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('search');
  const [mapViewType, setMapViewType] = useState<MapViewType>('search-results');
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState<boolean>(false);
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);

  // No longer requesting user location

  // No need to create initial list - myRoutes starts empty

  // Offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      showToastMessage('Connection restored');
    };

    const handleOffline = () => {
      setIsOffline(true);
      showToastMessage('You are now offline - using cached data');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const hideToast = () => {
    setShowToast(false);
  };

  const handleToggleMyRoute = (route: Route) => {
    if (isRouteInMyRoutes(route)) {
      removeRoute(route);
      showToastMessage(`Removed "${route.Route}" from My Routes`);
    } else {
      addRoute(route);
      showToastMessage(`Added "${route.Route}" to My Routes`);
    }
  };

  const handleViewMyRoutesOnMap = () => {
    setViewMode('map');
    setMapViewType('my-routes');
  };

  const handleMapRouteSelect = (route: Route) => {
    // Scroll to the route in the search results
    const routeElement = document.getElementById(`route-${route.Route.replace(/\s+/g, '-')}`);
    if (routeElement) {
      routeElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleViewRouteOnMap = (route: Route) => {
    setSelectedRoute(route);
    setMapViewType('selected-route');
    setViewMode('map');
  };


  if (loading || cragsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800">Loading Routes...</h2>
          <p className="text-gray-600">Fetching Red River Gorge climbing data</p>
        </div>
      </div>
    );
  }

  if (error || cragsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <h2 className="text-xl font-semibold mb-2">Error Loading Data</h2>
            <p>{error || cragsError}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Layout */}
          <div className="sm:hidden py-4">
            {/* Title */}
            <div className="flex items-center justify-center mb-4">
              <h1 className="text-xl font-bold text-gray-900 flex items-center">
                <img 
                  src="/biner.png" 
                  alt="Carabiner" 
                  className="w-6 h-6 mr-2"
                />
                Climb the Red <span className="w-2 h-2 bg-red-500 rounded-full mx-2"></span> Route Finder
                {isOffline && (
                  <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                    Offline
                  </span>
                )}
              </h1>
            </div>
            
            
            {/* View Mode Tabs */}
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('search')}
                className={`flex items-center justify-center flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'search'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List className="w-4 h-4 mr-2" />
                Search
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center justify-center flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'map'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MapPin className="w-4 h-4 mr-2" />
                Map
              </button>
              <button
                onClick={() => setViewMode('my-routes')}
                className={`flex items-center justify-center flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'my-routes'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Settings className="w-4 h-4 mr-2" />
                My Routes
              </button>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden sm:flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                <img 
                  src="/biner.png" 
                  alt="Carabiner" 
                  className="w-6 h-6 mr-2"
                />
                Climb the Red <span className="w-2 h-2 bg-red-500 rounded-full mx-2"></span> Route Finder
                {isOffline && (
                  <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                    Offline
                  </span>
                )}
              </h1>
              
            </div>
            
            {/* View Mode Tabs */}
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('search')}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'search'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List className="w-4 h-4 mr-2" />
                Search
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'map'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MapPin className="w-4 h-4 mr-2" />
                Map
              </button>
              <button
                onClick={() => setViewMode('my-routes')}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'my-routes'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Settings className="w-4 h-4 mr-2" />
                My Routes
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {viewMode === 'search' && (
          <div className="space-y-6">
            {/* Search Filters */}
            <SearchFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filters={filters}
              onFiltersChange={updateFilters}
              onClearFilters={clearFilters}
              routes={routes}
            />

            {/* Results Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                {totalResults} route{totalResults !== 1 ? 's' : ''} found
              </h2>
              <SortByControl
                filters={filters}
                onFiltersChange={updateFilters}
              />
            </div>

            {/* Route Groups */}
            <div className="space-y-8">
              {groupedRoutes.map((group: RouteGroupType) => (
                <RouteGroup
                  key={`${group.area}-${group.crag}`}
                  group={group}
                  userLocation={userLocation || undefined}
                  isRouteInBelayList={isRouteInMyRoutes}
                  onToggleBelayList={handleToggleMyRoute}
                  showPitches={filters.minPitches !== undefined || filters.maxPitches !== undefined}
                  onViewOnMap={handleViewRouteOnMap}
                  cragsData={crags}
                />
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
              onPrevious={prevPage}
              onNext={nextPage}
              totalResults={totalResults}
              pageSize={pageSize}
            />

            {totalResults === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No routes match your search criteria.</p>
                <button
                  onClick={clearFilters}
                  className="btn-primary mt-4"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}

        {viewMode === 'map' && (
          <div className="space-y-6">
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Route Map</h2>
                <MapViewControl
                  currentView={mapViewType}
                  onViewChange={setMapViewType}
                  myRoutesCount={myRoutes.length}
                  selectedRouteName={selectedRoute?.Route}
                />
              </div>
              <RouteMap
                routes={mapViewType === 'my-routes' ? myRoutes : mapViewType === 'selected-route' && selectedRoute ? [selectedRoute] : filteredRoutes}
                userLocation={userLocation || undefined}
                selectedRoutes={myRoutes}
                onRouteSelect={handleMapRouteSelect}
                selectedRoute={selectedRoute}
                className="w-full"
              />
            </div>
          </div>
        )}

        {viewMode === 'my-routes' && (
          <MyRoutes
            myRoutes={myRoutes}
            onRemoveRoute={removeRoute}
            onClearAll={clearAllRoutes}
            userLocation={userLocation || undefined}
            showPitches={filters.minPitches !== undefined || filters.maxPitches !== undefined}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-gray-600">
            {/* Support the app section */}
            <div className="mb-4">
              <a
                href="https://buymeacoffee.com/theredroutefinder"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Support the app • 50% of proceeds go to RRGCC
              </a>
            </div>
            <p className="text-sm">
              Climb the Red Route Finder • Data from Mountain Project
            </p>
            <p className="text-xs mt-2">
              Always climb responsibly and follow local regulations
            </p>
          </div>
        </div>
      </footer>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={hideToast}
      />
    </div>
  );
}

export default App;
